import { Box, Button, Typography, Chip, Divider, CircularProgress } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import DialogWrapper from "components/DialogWrapper";
import { analyzeAttachmentFromCloud } from "api/services/attatchment-ai-summary";
import { snack } from "./toast";
import { useMutation } from "react-query";

/* ================= TYPES ================= */

interface AiSection {
    index: number;
    title: string;
    content: string;
}

interface Props {
    notice: any;
    buttonVariant?: "text" | "outlined" | "contained";
    buttonLabel?: string;
}

interface IDrawerWrapperProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    title: string;   // ⬅ REQUIRED
    width?: "sm" | "md" | "lg";
}

/* ================= PARSER ================= */

// const parseAiResponse = (text: string): AiSection[] => {
//     if (!text) return [];

//     const regex =
//         /(?:^|\n)##+\s*(\d+)\.\s*(.+?)\n([\s\S]*?)(?=\n##+\s*\d+\.|\s*$)/g;

//     const sections: AiSection[] = [];
//     let match: RegExpExecArray | null;
//     let idx = 1;

//     while ((match = regex.exec(text)) !== null) {
//         sections.push({
//             index: idx++,
//             title: match[2].trim(),
//             content: match[3].trim().replace(/^-\s\[\s\]\s/gm, '• '),
//         });
//     }

//     return sections;
// };

const parseAiResponse = (text: string): AiSection[] => {
    if (!text || typeof text !== "string") return [];

    const cleanText = text.trim();
    if (!cleanText) return [];

    const sections: AiSection[] = [];

    /**
     * Header pattern supporting:
     * - ## 1. Title or ## 1) Title or ## Title
     * - **1. Title** or **1) Title** or **Title**
     * - 1. Title or 1) Title at line start
     */
    const headerRegex =
        /(?:^|\n)(?:#{2,}\s*(?:(\d+)[\.\)\-:]?\s*)?([^\n]+)|\*\*(?:(\d+)[\.\)\-:]?\s*)?([^*]+?)\*\*|(\d+)[\.\)]\s+([A-Z][^\n]+))\n/g;

    const headers: {
        index: number;
        title: string;
        contentStart: number;
        matchStart: number;
    }[] = [];
    let match: RegExpExecArray | null;

    while ((match = headerRegex.exec(cleanText)) !== null) {
        const rawNum = match[1] || match[3] || match[5];
        let rawTitle = match[2] || match[4] || match[6] || "";

        // Clean up markdown markers from title
        rawTitle = rawTitle.replace(/[*#_]/g, "").trim();

        // Skip headers that are excessively long (likely body text) or empty
        if (!rawTitle || rawTitle.length > 120) continue;

        const num =
            rawNum && !isNaN(Number(rawNum))
                ? Number(rawNum)
                : headers.length + 1;

        headers.push({
            index: num,
            title: rawTitle,
            contentStart: match.index + match[0].length,
            matchStart: match.index,
        });
    }

    for (let i = 0; i < headers.length; i++) {
        const current = headers[i];
        const nextStart =
            i + 1 < headers.length ? headers[i + 1].matchStart : cleanText.length;
        const content = cleanText
            .substring(current.contentStart, nextStart)
            .trim()
            .replace(/^\-\s+/gm, "• ");

        sections.push({
            index: current.index,
            title: current.title,
            content: content,
        });
    }

    // Sort to maintain natural sequence
    sections.sort((a, b) => a.index - b.index);

    // Fallback: If no headers were matched but text is present, display full text
    if (sections.length === 0 && cleanText.length > 0) {
        sections.push({
            index: 1,
            title: "Litigation Analysis",
            content: cleanText.replace(/^\-\s+/gm, "• "),
        });
    }

    return sections;
};

const renderBoldText = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);

    return parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return (
                <strong key={index}>
                    {part.slice(2, -2)}
                </strong>
            );
        }
        return <span key={index}>{part}</span>;
    });
};



/* ================= COMPONENT ================= */

const AttachmentAISummary = ({
    notice,
    buttonVariant = "outlined",
    buttonLabel = " Quick Summary",
}: Props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sections, setSections] = useState<AiSection[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // const handleClick = async () => {
    //     try {
    //         setLoading(true);

    //         const res: any = await analyzeAttachmentFromCloud({
    //             notice,
    //         });
    //         const parsed = parseAiResponse(res.data.assistantMessage);
    //         setSections(parsed);
    //         setSelectedIndex(0);
    //         setOpen(true);
    //     } catch (err:any) {
    //         console.error("AI Summary Error:", err);
    //         snack.error(err);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const analyzeMutation = useMutation(
  (payload: any) => analyzeAttachmentFromCloud(payload),
  {
    onSuccess: (res: any) => {
      const parsed = parseAiResponse(res.data.assistantMessage);
      setSections(parsed);
      setSelectedIndex(0);
      setOpen(true);
    },

    onError: (err: any) => {
      const backendError = err?.response?.data?.message;

      // 🔐 Google re-auth
      if (backendError === 'invalid_grant') {
        snack.error('Storage Drive access expired. Please re-authorize.');
        return;
      }

      // 🔴 Generic error
      const message =
        typeof backendError === 'string'
          ? backendError
          : backendError?.message || 'Something went wrong';

      snack.error(message);
    },

    onSettled: () => {
      setLoading(false);
    },
  }
);

    const handleClick = () => {
  setLoading(true);

  analyzeMutation.mutate({
    notice,
  });
};

    return (
        <>
            <Button
                variant={buttonVariant}
                color="primary"
                disabled={loading}
                onClick={handleClick}
                startIcon={
                    loading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        "✨"
                    )
                }
                sx={{
                    borderRadius: "50px", // Makes the button fully rounded (pill-shaped)
                    textTransform: "none", // Optional: Prevents all-caps for a cleaner look
                    px: 2, // Horizontal padding to suit the rounded ends
                    fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)", // Soft primary-colored shadow
                    transition: "all 0.3s ease", // Smooth hover effect
                    "&:hover": {
                        boxShadow: "0 6px 16px rgba(25, 118, 210, 0.35)", // Stronger shadow on hover
                        transform: "translateY(-1px)", // Slight lift on hover
                    },
                    "&:disabled": {
                        boxShadow: "none", // Remove shadow when loading/disabled
                    }
                }}
            >
                {loading ? " Analyzing..." : buttonLabel}
            </Button>

            <DialogWrapper open={open} setOpen={setOpen} width="lg" title="✨ Vider AI Summary">
                <Box>
                    {/* ================= HEADER ================= */}
                    <Box
                        px={3}
                        py={1}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        borderBottom="1px solid #e6e9ef"

                    >
                        <Box
                            width={36}
                            height={36}
                            borderRadius="50%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="#e8f2ff"
                        >
                            ✨
                        </Box>

                        <Typography variant="h6" fontWeight={600}>
                            Vider AI Summary
                            <Typography
                                component="span"
                                ml={1}
                                color="text.secondary"
                                fontWeight={400}
                            >
                                | Advanced Litigation Intelligence Suite
                            </Typography>
                        </Typography>
                    </Box>

                    {/* ================= BODY ================= */}
                    <Box display="flex" height="72vh">
                        {/* ===== LEFT PANEL ===== */}
                        <Box
                            width="32%"
                            p={2}
                            borderRight="1px solid #e6e9ef"
                            sx={{
                                overflowY: "auto",
                                overscrollBehavior: "contain"
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                            >
                                <Typography fontWeight={600}>Analysis Topics</Typography>
                                <Chip
                                    label={`${sections.length} Topics`}
                                    size="small"
                                    color="primary"
                                />
                            </Box>

                            {sections.map((section, idx) => {
                                const active = idx === selectedIndex;

                                return (
                                    <Box
                                        key={idx}
                                        mb={1.2}
                                        px={2}
                                        py={1.5}
                                        borderRadius={2}
                                        display="flex"
                                        alignItems="center"
                                        gap={2}
                                        sx={{
                                            cursor: "pointer",
                                            border: active
                                                ? "1.5px solid #1976d2"
                                                : "1px solid #e0e0e0",
                                            backgroundColor: active ? "#f4f8ff" : "#fff",
                                            boxShadow: active
                                                ? "0 2px 8px rgba(25,118,210,0.15)"
                                                : "none",
                                        }}
                                        onClick={() => setSelectedIndex(idx)}
                                    >
                                        <Box
                                            width={28}
                                            height={28}
                                            borderRadius={1}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontWeight={600}
                                            bgcolor={active ? "#1976d2" : "#f1f1f1"}
                                            color={active ? "#fff" : "#555"}
                                        >
                                            {section.index}
                                        </Box>

                                        <Typography
                                            fontSize="0.9rem"
                                            fontWeight={active ? 600 : 500}
                                            color={active ? "text.primary" : "text.secondary"}
                                        >
                                            {section.title}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* ===== RIGHT PANEL ===== */}
                        <Box
                            width="68%"
                            p={3}
                            bgcolor="#fafbfd"
                        >
                            {sections[selectedIndex] && (
                                <Box
                                    bgcolor="#fff"
                                    border="1px solid #e6e9ef"
                                    borderRadius={3}
                                    p={3}
                                    height="100%"
                                    display="flex"
                                    flexDirection="column"
                                    boxShadow="0 4px 16px rgba(0,0,0,0.05)"
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mb={2}
                                    >
                                        <Typography variant="h6" fontWeight={600}>
                                            {sections[selectedIndex].title}
                                        </Typography>

                                        <Chip
                                            label={`Topic ${sections[selectedIndex].index}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>

                                    <Divider sx={{ mb: 2 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            DETAILED ANALYSIS
                                        </Typography>
                                    </Divider>

                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            overflowY: "auto", // Enables vertical scrolling
                                            pr: 1, // Padding right for the scrollbar space
                                            '&::-webkit-scrollbar': {
                                                width: '6px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#e0e0e0',
                                                borderRadius: '10px',
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}
                                        >
                                            {renderBoldText(sections[selectedIndex].content)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogWrapper>


        </>
    );
};

export default AttachmentAISummary;
